import { MatchType } from '../../interface';

export const BRACKET_MATCH_HEIGHT = 120;
export const BRACKET_CARD_WIDTH = 240;
export const BRACKET_CARD_HEIGHT = 100;
export const BRACKET_BYE_CARD_HEIGHT = 70;
export const BRACKET_COLUMN_INNER = 300;
export const BRACKET_COLUMN_GAP = 24;
export const BRACKET_COLUMN_WIDTH = BRACKET_COLUMN_INNER + BRACKET_COLUMN_GAP;
export const BRACKET_SIDE_PADDING_Y = 16;
export const BRACKET_ROUND_HEADER_HEIGHT = 40;

export type BracketLayoutMatch = {
    id: string;
    round: number;
    type: number;
    next_match_id?: string;
    next_loser_match_id?: string;
    bout_number?: string;
    rank?: number;
    state?: string;
    // The only real match feeding a bout whose other side is a bye. The bout is
    // pushed down one card, the same step a single extra match draws.
    attached_feeder?: boolean;
};

export type BracketRoundPosition = {
    round: number;
    index: number;
    bout: string;
    parent_matches: string[];
    top: number;
};

export type BracketConnectorKind = 'winner' | 'loser';

export type BracketConnector = {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    startMatchId: string;
    endMatchId: string;
    kind: BracketConnectorKind;
};

export type BracketViewLayout<T extends BracketLayoutMatch = BracketLayoutMatch> = {
    leftMatches: T[];
    rightMatches: T[];
    centerMatches: T[];
    leftRounds: number[];
    rightRounds: number[];
    leftPositions: Record<string, BracketRoundPosition>;
    rightPositions: Record<string, BracketRoundPosition>;
    centerPositions: Record<string, BracketRoundPosition>;
    centerMargin: number;
    connectors: BracketConnector[];
};

type BracketSide = 'left' | 'right' | 'center';

function matchLayoutKey(match: { id: string; rank?: number }): string {
    if (typeof match.rank === 'number') {
        return String(match.rank).padStart(8, '0');
    }
    return match.id;
}

function compareLayoutOrder(
    left: { id: string; rank?: number },
    right: { id: string; rank?: number },
): number {
    return matchLayoutKey(left).localeCompare(matchLayoutKey(right), undefined, { numeric: true });
}

export function indexMatchesByNextId<T extends { id: string; next_match_id?: string; rank?: number }>(
    matches: T[],
): Map<string, T[]> {
    const byNext = new Map<string, T[]>();
    for (const match of matches) {
        if (!match.next_match_id) {
            continue;
        }
        const list = byNext.get(match.next_match_id);
        if (list) {
            list.push(match);
        } else {
            byNext.set(match.next_match_id, [match]);
        }
    }
    byNext.forEach(list => list.sort(compareLayoutOrder));
    return byNext;
}

export function uniqueSortedRounds(matches: Array<{ round: number }>): number[] {
    return Array.from(new Set(matches.map(match => match.round))).sort((left, right) => left - right);
}

function subtreeAnchor<T extends BracketLayoutMatch>(
    root: T,
    byNext: Map<string, T[]>,
): { round: number; key: string } {
    let best = { round: root.round, key: matchLayoutKey(root) };
    const seen = new Set<string>([root.id]);
    const queue = [...(byNext.get(root.id) || [])];
    while (queue.length > 0) {
        const match = queue.shift()!;
        if (seen.has(match.id)) {
            continue;
        }
        seen.add(match.id);
        const key = matchLayoutKey(match);
        if (
            match.round < best.round ||
            (match.round === best.round && key.localeCompare(best.key, undefined, { numeric: true }) < 0)
        ) {
            best = { round: match.round, key };
        }
        queue.push(...(byNext.get(match.id) || []));
    }
    return best;
}

function isAnchoredFeeder(match: BracketLayoutMatch): boolean {
    return match.type === MatchType.ExtraMatch || Boolean(match.attached_feeder);
}

// A pushed target can land on the next card in the same column. Keep one
// card of space. This does not move a card upward.
function separateStackedCards(
    roundMatches: BracketLayoutMatch[],
    positions: Record<string, BracketRoundPosition>,
) {
    const ordered = roundMatches
        .filter(match => positions[match.id])
        .slice()
        .sort((left, right) => {
            const delta = positions[left.id].top - positions[right.id].top;
            if (delta !== 0) {
                return delta;
            }
            return positions[left.id].index - positions[right.id].index;
        });
    let previousTop = -BRACKET_MATCH_HEIGHT;
    ordered.forEach(match => {
        const position = positions[match.id];
        if (position.top < previousTop + BRACKET_MATCH_HEIGHT) {
            position.top = previousTop + BRACKET_MATCH_HEIGHT;
        }
        previousTop = position.top;
    });
}

function compareAnchors(left: { round: number; key: string }, right: { round: number; key: string }): number {
    if (left.round !== right.round) {
        return left.round - right.round;
    }
    return left.key.localeCompare(right.key, undefined, { numeric: true });
}

function orderMatchesByNextGroup(
    roundMatches: BracketLayoutMatch[],
    byNext: Record<string, BracketLayoutMatch[]>,
    matchById: Map<string, BracketLayoutMatch>,
): BracketLayoutMatch[] {
    const nextIds = Object.keys(byNext).sort((leftId, rightId) => {
        const left = matchById.get(leftId);
        const right = matchById.get(rightId);
        if (left && right) {
            return compareLayoutOrder(left, right);
        }
        return leftId.localeCompare(rightId);
    });
    const used = new Set<string>();
    const ordered: BracketLayoutMatch[] = [];
    for (const nextId of nextIds) {
        for (const match of byNext[nextId] || []) {
            ordered.push(match);
            used.add(match.id);
        }
    }
    const leftover = roundMatches
        .filter(match => !used.has(match.id))
        .slice()
        .sort(compareLayoutOrder);
    return ordered.concat(leftover);
}

function collectPreceding<T extends { id: string; next_match_id?: string }>(
    root: T,
    byNext: Map<string, T[]>,
): T[] {
    const result: T[] = [];
    const seen = new Set<string>();
    const queue = [...(byNext.get(root.id) || [])];
    while (queue.length > 0) {
        const match = queue.shift()!;
        if (seen.has(match.id)) {
            continue;
        }
        seen.add(match.id);
        result.push(match);
        queue.push(...(byNext.get(match.id) || []));
    }
    if (!result.some(match => match.id === root.id)) {
        result.push(root);
    }
    return result;
}

export function splitBracketHalves<T extends BracketLayoutMatch>(
    matches: T[],
): { leftMatches: T[]; rightMatches: T[]; centerMatches: T[] } {
    const empty = { leftMatches: [] as T[], rightMatches: [] as T[], centerMatches: [] as T[] };
    if (!matches.length) {
        return empty;
    }
    const finalMatch = matches.find(match => match.type === MatchType.Final);
    if (!finalMatch) {
        return empty;
    }

    const byNext = indexMatchesByNextId(matches);
    const centerMatches = matches.filter(
        match => match.id === finalMatch.id || match.type === MatchType.ThirdPlace,
    );
    const predecessors = [...(byNext.get(finalMatch.id) || [])].sort((left, right) =>
        compareAnchors(subtreeAnchor(left, byNext), subtreeAnchor(right, byNext)),
    );

    if (predecessors.length === 0) {
        return { leftMatches: [], rightMatches: [], centerMatches };
    }
    if (predecessors.length === 1) {
        return {
            leftMatches: collectPreceding(predecessors[0], byNext),
            rightMatches: [],
            centerMatches,
        };
    }

    return {
        leftMatches: collectPreceding(predecessors[0], byNext),
        rightMatches: collectPreceding(predecessors[1], byNext),
        centerMatches,
    };
}

export function buildBracketSidePositions(
    matches: BracketLayoutMatch[],
    hasExtraMatches: boolean,
): Record<string, BracketRoundPosition> {
    const positions: Record<string, BracketRoundPosition> = {};
    const parentMap: Record<string, string[]> = {};
    const matchById = new Map(matches.map(match => [match.id, match]));
    const rounds = uniqueSortedRounds(matches);
    const firstRound = hasExtraMatches ? 1 : 0;
    const extraCountByNext: Record<string, number> = {};
    const extrasByNext: Record<string, BracketLayoutMatch[]> = {};
    const firstRoundByNext: Record<string, BracketLayoutMatch[]> = {};

    for (const match of matches) {
        if (isAnchoredFeeder(match) && match.next_match_id) {
            extraCountByNext[match.next_match_id] = (extraCountByNext[match.next_match_id] || 0) + 1;
            extrasByNext[match.next_match_id] = extrasByNext[match.next_match_id] || [];
            extrasByNext[match.next_match_id].push(match);
        }
        if (match.round !== firstRound || match.type === MatchType.ExtraMatch || !match.next_match_id) {
            continue;
        }
        firstRoundByNext[match.next_match_id] = firstRoundByNext[match.next_match_id] || [];
        firstRoundByNext[match.next_match_id].push(match);
    }
    Object.values(extrasByNext).forEach(list => list.sort(compareLayoutOrder));
    Object.values(firstRoundByNext).forEach(list => list.sort(compareLayoutOrder));

    for (const round of rounds) {
        const roundMatches =
            round === firstRound
                ? orderMatchesByNextGroup(
                    matches.filter(item => item.round === firstRound && item.type !== MatchType.ExtraMatch),
                    firstRoundByNext,
                    matchById,
                )
                : matches.filter(match => match.round === round).slice().sort(compareLayoutOrder);

        let currentTop = 0;
        roundMatches.forEach((match, index) => {
            const parents = parentMap[match.id] || [];
            let top = currentTop;
            if (round !== firstRound && parents.length > 0) {
                const firstParent = positions[parents[0]];
                const lastParent = positions[parents[parents.length - 1]];
                if (firstParent && lastParent) {
                    top = Math.max(0, (firstParent.top + lastParent.top) / 2);
                }
            }
            if ((extraCountByNext[match.id] || 0) > 0) {
                top += BRACKET_MATCH_HEIGHT;
            }
            if (match.next_match_id) {
                parentMap[match.next_match_id] = parentMap[match.next_match_id] || [];
                parentMap[match.next_match_id].push(match.id);
            }
            positions[match.id] = {
                bout: match.bout_number || '',
                round,
                index,
                top,
                parent_matches: [],
            };
            const extraCount = extraCountByNext[match.id] || 0;
            // A bye feeder's bout is drawn one card lower. The next feeder has
            // to start below that bout, or its connector runs into the previous one.
            const slotHeight = match.attached_feeder ? BRACKET_MATCH_HEIGHT * 2 : BRACKET_MATCH_HEIGHT;
            currentTop += extraCount > 0
                ? BRACKET_MATCH_HEIGHT + extraCount * BRACKET_MATCH_HEIGHT
                : slotHeight;
        });
        separateStackedCards(roundMatches, positions);
    }

    for (const [id, position] of Object.entries(positions)) {
        const match = matchById.get(id);
        // A bye feeder stays in its own slot. Only a real extra match is
        // pulled out of that slot and redrawn against the bout it feeds.
        if (match?.type !== MatchType.ExtraMatch || !match.next_match_id) {
            continue;
        }
        const nextPosition = positions[match.next_match_id];
        if (!nextPosition) {
            continue;
        }
        const extras = extrasByNext[match.next_match_id] || [];
        const extraIndex = extras.findIndex(item => item.id === match.id);
        if (extras.length === 2) {
            position.top = nextPosition.top + (extraIndex === 0 ? -BRACKET_MATCH_HEIGHT : BRACKET_MATCH_HEIGHT);
        } else {
            position.top = nextPosition.top - BRACKET_MATCH_HEIGHT;
        }
    }

    for (const [id, parents] of Object.entries(parentMap)) {
        if (positions[id]) {
            positions[id].parent_matches = parents;
        }
    }

    return positions;
}

export function finalCenterOffset(
    finalMatch: { id: string } | undefined,
    leftMatches: Array<{ id: string; next_match_id?: string }>,
    rightMatches: Array<{ id: string; next_match_id?: string }>,
    leftPositions: Record<string, BracketRoundPosition>,
    rightPositions: Record<string, BracketRoundPosition>,
): number {
    if (!finalMatch) {
        return 0;
    }
    const leftSemi = leftMatches.find(match => match.next_match_id === finalMatch.id);
    const rightSemi = rightMatches.find(match => match.next_match_id === finalMatch.id);
    const leftTop = leftSemi ? leftPositions[leftSemi.id]?.top ?? 0 : 0;
    const rightTop = rightSemi ? rightPositions[rightSemi.id]?.top ?? 0 : 0;
    if (!leftSemi && !rightSemi) {
        return 0;
    }
    return Math.max(0, (leftTop + rightTop) / 2);
}

export function buildCenterPositions(
    centerMatches: BracketLayoutMatch[],
    centerMargin: number,
): Record<string, BracketRoundPosition> {
    const positions: Record<string, BracketRoundPosition> = {};
    const finalMatch = centerMatches.find(match => match.type === MatchType.Final);
    const thirdMatch = centerMatches.find(match => match.type === MatchType.ThirdPlace);
    if (finalMatch) {
        positions[finalMatch.id] = {
            bout: finalMatch.bout_number || '',
            round: finalMatch.round,
            index: 0,
            top: centerMargin,
            parent_matches: [],
        };
    }
    if (thirdMatch) {
        positions[thirdMatch.id] = {
            bout: thirdMatch.bout_number || '',
            round: thirdMatch.round,
            index: 1,
            top: centerMargin + BRACKET_MATCH_HEIGHT,
            parent_matches: [],
        };
    }
    return positions;
}

function cardHeight(match: BracketLayoutMatch): number {
    return match.state === 'bye' ? BRACKET_BYE_CARD_HEIGHT : BRACKET_CARD_HEIGHT;
}

function columnCardX(
    side: BracketSide,
    round: number,
    leftRounds: number[],
    rightRounds: number[],
): number {
    const leftWidth = leftRounds.length * BRACKET_COLUMN_WIDTH;
    if (side === 'center') {
        return leftWidth + BRACKET_COLUMN_GAP / 2;
    }
    if (side === 'left') {
        const index = Math.max(0, leftRounds.indexOf(round));
        return index * BRACKET_COLUMN_WIDTH + BRACKET_COLUMN_GAP / 2;
    }
    const index = Math.max(0, rightRounds.indexOf(round));
    const fromCenter = Math.max(0, rightRounds.length - 1 - index);
    return leftWidth + BRACKET_COLUMN_WIDTH + fromCenter * BRACKET_COLUMN_WIDTH + BRACKET_COLUMN_GAP / 2;
}

function cardBox(
    match: BracketLayoutMatch,
    side: BracketSide,
    position: BracketRoundPosition,
    leftRounds: number[],
    rightRounds: number[],
): { x: number; y: number; width: number; height: number } {
    return {
        x: columnCardX(side, match.round, leftRounds, rightRounds),
        y: BRACKET_SIDE_PADDING_Y + BRACKET_ROUND_HEADER_HEIGHT + position.top,
        width: BRACKET_CARD_WIDTH,
        height: cardHeight(match),
    };
}

function connectorY(box: { y: number; height: number }): number {
    return box.y + box.height / 2;
}

export function buildBracketConnectors(
    params: {
        matches: BracketLayoutMatch[];
        leftMatches: BracketLayoutMatch[];
        rightMatches: BracketLayoutMatch[];
        centerMatches: BracketLayoutMatch[];
        leftPositions: Record<string, BracketRoundPosition>;
        rightPositions: Record<string, BracketRoundPosition>;
        centerPositions: Record<string, BracketRoundPosition>;
        leftRounds: number[];
        rightRounds: number[];
    },
    options: { drawLoserConnectors?: boolean } = {},
): BracketConnector[] {
    const matchById = new Map(params.matches.map(match => [match.id, match]));
    const locate = (id: string): { match: BracketLayoutMatch; side: BracketSide; position: BracketRoundPosition } | null => {
        const match = matchById.get(id);
        if (!match) {
            return null;
        }
        if (params.leftPositions[id]) {
            return { match, side: 'left', position: params.leftPositions[id] };
        }
        if (params.rightPositions[id]) {
            return { match, side: 'right', position: params.rightPositions[id] };
        }
        if (params.centerPositions[id]) {
            return { match, side: 'center', position: params.centerPositions[id] };
        }
        return null;
    };

    const edge = (
        start: { match: BracketLayoutMatch; side: BracketSide; position: BracketRoundPosition },
        end: { match: BracketLayoutMatch; side: BracketSide; position: BracketRoundPosition },
        kind: BracketConnectorKind,
    ): BracketConnector => {
        const startBox = cardBox(start.match, start.side, start.position, params.leftRounds, params.rightRounds);
        const endBox = cardBox(end.match, end.side, end.position, params.leftRounds, params.rightRounds);
        const towardRight = start.side === 'left' || (start.side === 'center' && end.side === 'right');
        return {
            startX: towardRight ? startBox.x + startBox.width : startBox.x,
            startY: connectorY(startBox),
            endX: towardRight ? endBox.x : endBox.x + endBox.width,
            endY: connectorY(endBox),
            startMatchId: start.match.id,
            endMatchId: end.match.id,
            kind,
        };
    };

    const connectors: BracketConnector[] = [];
    const sources = [...params.leftMatches, ...params.rightMatches, ...params.centerMatches];
    for (const source of sources) {
        const start = locate(source.id);
        if (!start) {
            continue;
        }
        if (source.next_match_id) {
            const end = locate(source.next_match_id);
            if (end) {
                connectors.push(edge(start, end, 'winner'));
            }
        }
        if (options.drawLoserConnectors && source.next_loser_match_id) {
            const end = locate(source.next_loser_match_id);
            if (end) {
                connectors.push(edge(start, end, 'loser'));
            }
        }
    }
    return connectors;
}

export function layoutBracketView<T extends BracketLayoutMatch>(
    matches: T[],
    hasExtraMatches: boolean,
    options: { drawLoserConnectors?: boolean } = {},
): BracketViewLayout<T> {
    const { leftMatches, rightMatches, centerMatches } = splitBracketHalves(matches);
    const leftRounds = uniqueSortedRounds(leftMatches);
    const rightRounds = uniqueSortedRounds(rightMatches);
    const leftPositions = buildBracketSidePositions(leftMatches, hasExtraMatches);
    const rightPositions = buildBracketSidePositions(rightMatches, hasExtraMatches);
    const centerMargin = finalCenterOffset(
        matches.find(match => match.type === MatchType.Final),
        leftMatches,
        rightMatches,
        leftPositions,
        rightPositions,
    );
    const centerPositions = buildCenterPositions(centerMatches, centerMargin);
    const connectors = buildBracketConnectors({
        matches,
        leftMatches,
        rightMatches,
        centerMatches,
        leftPositions,
        rightPositions,
        centerPositions,
        leftRounds,
        rightRounds,
    }, options);
    return {
        leftMatches,
        rightMatches,
        centerMatches,
        leftRounds,
        rightRounds,
        leftPositions,
        rightPositions,
        centerPositions,
        centerMargin,
        connectors,
    };
}
